import { BriefPage } from 'features/briefs/components/brief-page'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { BRIEF_CONTENT_TAB } from 'features/briefs/utils/nav'
import HTMLEditor from 'components/ui/html-editor'
import Row from 'components/common/row'
import { IconButton, TextField } from 'components/ds'
import Label from 'components/ui/label'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { updateBrief } from '@disco/state-management/reducers/entities/briefs'
import { getBrief } from '@disco/state-management/reducers/entities/briefs'
import { useCallback, useMemo } from 'react'
import styles from './index.module.css'
import { EmailSchedulerModal } from 'components/modals/email-scheduler'
import { ScheduledValue } from 'types/campaign'
import {
  formatScheduledCampaignData,
  getTimeDateToScheduleEmail,
} from '@disco/utils/emailCampaigns'
import Icon from 'components/ui/icon'

const rowSizes = {
  8_4: [8, 4],
  12: [12],
}

export const BriefEditorContentPage = () => {
  const params = useParams<{ briefId: string }>()
  const dispatch = useDispatch()
  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(params.briefId!) }),
  )

  const [isDeadlinePickerOpen, setIsDeadlinePickerOpen] = React.useState(false)

  const campaignSchedule = formatScheduledCampaignData({
    scheduled_at: brief?.deadline,
    scheduled_at_timezone: brief?.deadline_timezone,
    status: 'draft', // doesn't matter, not used here
  })

  const onDescriptionChange = useCallback(
    (description, descriptionHtml) => {
      if (brief?.description_html !== descriptionHtml) {
        dispatch(
          updateBrief({
            ...brief,
            description: JSON.stringify(description),
            description_html: descriptionHtml,
          }),
        )
      }
    },
    [brief, dispatch],
  )

  const initialContent = useMemo(() => {
    if (brief?.description) {
      try {
        return JSON.parse(brief?.description)
      } catch (error) {
        return null
      }
    }
  }, [])

  const _handleSetDeadline = ({
    date,
    time,
    timeZone,
  }: {
    date: string
    time: ScheduledValue
    timeZone: ScheduledValue
  }) => {
    const deadlineToScheduleEmail = getTimeDateToScheduleEmail(
      date as any,
      time.value,
      timeZone.value,
    )

    dispatch(
      updateBrief({
        ...brief,
        deadline: deadlineToScheduleEmail,
        deadline_timezone: timeZone.value,
      }),
    )
  }

  return (
    <BriefPage step={BRIEF_CONTENT_TAB} title={BRIEF_CONTENT_TAB}>
      <Row columnsSize={rowSizes[8_4]}>
        <div>
          <Label caption="Brief name" />
          <TextField.Root
            size="2"
            type="text"
            autoComplete="off"
            value={brief?.name}
            onChange={event => {
              dispatch(updateBrief({ ...brief, name: event.target.value }))
            }}
            autoFocus
          />
        </div>
        <div>
          <Label caption="Brief deadline" />
          <TextField.Root
            size="2"
            id="deadline"
            type="text"
            value={
              brief?.deadline
                ? `${campaignSchedule?.date.label}, ${campaignSchedule?.time.label} ${campaignSchedule?.timeZone.value}`
                : ''
            }
            onClick={() => {
              setIsDeadlinePickerOpen(true)
            }}
            onChange={event => {
              setIsDeadlinePickerOpen(true)
            }}
          >
            {brief?.deadline && (
              <TextField.Slot
                side="right"
                tabIndex={-1}
                style={{ cursor: 'pointer' }}
              >
                <IconButton
                  size="1"
                  tabIndex={-1}
                  variant="ghost"
                  color="gray"
                  onClick={() => {
                    dispatch(
                      updateBrief({
                        ...brief,
                        deadline: null,
                        deadline_timezone: null,
                      }),
                    )
                  }}
                >
                  <Icon type="close" width={16} height={16} />
                </IconButton>
              </TextField.Slot>
            )}
            <TextField.Slot side="right" tabIndex={-1}>
              <IconButton
                size="1"
                tabIndex={-1}
                variant="ghost"
                color="gray"
                onClick={() => {
                  setIsDeadlinePickerOpen(true)
                }}
              >
                <Icon type="calendar" width={16} height={16} />
              </IconButton>
            </TextField.Slot>
          </TextField.Root>
        </div>
      </Row>
      <Row columnsSize={rowSizes[12]}>
        <div>
          <Label caption="Brief description" />
          <HTMLEditor
            initialContent={initialContent}
            contentClassName={styles.editorContent}
            placeholder={`Include key details like usage type, media, term, territory, mood, lyrical themes, reference tracks, and any creative or licensing restrictions.`}
            onChange={onDescriptionChange}
            ds
          />
        </div>
      </Row>
      {isDeadlinePickerOpen && (
        <EmailSchedulerModal
          onClose={() => setIsDeadlinePickerOpen(false)}
          setScheduleData={_handleSetDeadline}
          storedCampaignSchedule={campaignSchedule}
          caption="Set Brief Deadline"
          description="Select a date, time and timezone."
          primaryButtonCaption="Done"
        />
      )}
    </BriefPage>
  )
}
